<template>
  <f7-page name="community-health">
    <f7-navbar :title="$t('growth.health.title')"  back-link />
      <AppLayout>
  <template #main>
    <f7-block class="pb-0 growth-page no-margin no-padding">
      <f7-block v-if="loading" class="loading-block">
        <f7-preloader color="green" />
      </f7-block>

      <template v-else>
        <!-- Overall Health Score -->
        <f7-card class="growth-card health-hero mb-3">
          <f7-card-content>
            <div class="text-align-center">
              <div class="health-ring" :style="{ borderColor: healthMeta.color }">
                <span class="t-h2 t-bold" :style="{ color: healthMeta.color }">{{ health.score }}%</span>
              </div>
              <div class="t-subtitle t-bold">{{ $t('growth.health.overallHealth') }}</div>
              <f7-progressbar
                :progress="health.score"
                class="progress-leaf mt-2"
              ></f7-progressbar>
              <span class="health-badge" :style="{ background: healthMeta.bg, color: healthMeta.color }">
                {{ $t(healthMeta.label) }}
              </span>
            </div>
          </f7-card-content>
        </f7-card>

        <!-- Health Components -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="pulse" size="22" class="t-green" />
            <div class="t-bold">{{ $t('growth.health.components') }}</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list class="growth-list">
              <f7-list-item
                v-for="c in health.components"
                :key="c.name"
                :title="c.name"
                :subtitle="c.score + '%'"
              >
                <template #after>
                  <f7-progressbar
                    :progress="c.score"
                    class="progress-component"
                    :style="{ '--component-color': componentColor(c.name) }"
                    style="width: 100px"
                  ></f7-progressbar>
                </template>
              </f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <!-- Governance Details -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="shield_fill" size="22" class="t-blue" />
            <div class="t-bold">{{ $t('growth.health.governance') }} ({{ componentScore('Governance') }}%)</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list class="growth-list">
              <f7-list-item
                :title="$t('growth.health.committeeComplete')"
                :footer="governance.committeeComplete ? $t('growth.common.yes') : $t('growth.common.no')"
                :checkbox="governance.committeeComplete"
                :checked="governance.committeeComplete"
                disabled
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.meetingsHeld')"
                :footer="governance.meetingsHeld + ' / 12'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.votingCompleted')"
                :footer="governance.votingCompleted + ' votes'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.documentsUploaded')"
                :footer="governance.documentsUploaded + ' docs'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.policiesCreated')"
                :footer="governance.policiesCreated + ' policies'"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <!-- Financial Behavior -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="chart_pie_fill" size="22" class="t-green" />
            <div class="t-bold">{{ $t('growth.health.financialBehavior') }} ({{ componentScore('Finance') }}%)</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list class="growth-list">
              <f7-list-item
                :title="$t('growth.health.collectionsCompleted')"
                :footer="finance.collectionsCompleted + ' / 12'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.receiptsUploaded')"
                :footer="finance.receiptsUploaded + ' receipts'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.expensesApproved')"
                :footer="finance.expensesApproved + ' approved'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.financialReconciled')"
                :footer="finance.financialReconciled ? $t('growth.common.yes') : $t('growth.common.no')"
                :checkbox="finance.financialReconciled"
                :checked="finance.financialReconciled"
                disabled
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.disputes')"
                :footer="finance.disputes + ' disputes'"
                :footer-class="finance.disputes > 0 ? 'text-terracotta' : 'text-green'"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <!-- Participation -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="person_2_fill" size="22" class="t-leaf" />
            <div class="t-bold">{{ $t('growth.health.participation') }} ({{ componentScore('Participation') }}%)</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list class="growth-list">
              <f7-list-item
                :title="$t('growth.health.memberAttendance')"
                :footer="participation.attendanceRate + '%'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.activityRate')"
                :footer="participation.activityRate + '%'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.memberRetention')"
                :footer="participation.retentionRate + '%'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.responseRate')"
                :footer="participation.responseRate + '%'"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <!-- Marketplace Activity -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="store" size="22" class="t-purple" />
            <div class="t-bold">{{ $t('growth.health.marketplace') }} ({{ componentScore('Marketplace') }}%)</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list class="growth-list">
              <f7-list-item
                :title="$t('growth.health.listingsCreated')"
                :footer="marketplace.listingsCreated + ' listings'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.purchasesMade')"
                :footer="marketplace.purchasesMade + ' purchases'"
              ></f7-list-item>
              <f7-list-item
                :title="$t('growth.health.vendorRating')"
                :footer="marketplace.vendorRating + ' / 5'"
              >
                <template #after>
                  <div class="star-row">
                    <f7-icon
                      v-for="i in 5"
                      :key="i"
                      :f7="i <= marketplace.vendorRating ? 'star_fill' : 'star'"
                      :class="i <= marketplace.vendorRating ? 't-gold' : 'star-empty'"
                      size="20"
                    ></f7-icon>
                  </div>
                </template>
              </f7-list-item>
              <f7-list-item
                :title="$t('growth.health.successfulTransactions')"
                :footer="marketplace.successfulTransactions + ' / ' + marketplace.totalTransactions"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <!-- Improvement Tips -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="lightbulb" size="22" class="t-gold" />
            <div class="t-bold">{{ $t('growth.health.howToImprove') }}</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list v-if="improvementTips.length" class="growth-list">
              <f7-list-item
                v-for="tip in improvementTips"
                :key="tip.id"
                :title="tip.title"
                :subtitle="tip.description"
              ></f7-list-item>
            </f7-list>
            <div v-else class="t-muted text-align-center py-3">
              {{ $t('growth.health.greatShape') }}
            </div>
          </f7-card-content>
        </f7-card>
      </template>
    </f7-block>

    <f7-block class="pt-0">
      <f7-button large fill raised round class="btn-green" :href="links.dashboard" block>
        {{ $t('growth.health.backToGrowth') }}
      </f7-button>
    </f7-block>
     </template>
</AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  f7Page, f7Navbar, f7Block, f7Card, f7CardHeader, f7CardContent,
  f7List, f7ListItem, f7Progressbar, f7Icon, f7Button, f7Preloader
} from 'framework7-vue'
import AppLayout from '../components/common/AppLayout.vue'

import { useGrowthStore } from '../js/pinia-stores/growth.js'
import { healthMeta as healthMetaOf } from '../constants/growth-levels.js'
import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({ groupId: { type: String, default: '' } })

const growthStore = useGrowthStore()

const groupId = computed(() => props.groupId || '123')
const health = computed(() => growthStore.health)
const loading = computed(() => growthStore.loading)

const governance = ref({
  committeeComplete: false,
  meetingsHeld: 0,
  votingCompleted: 0,
  documentsUploaded: 0,
  policiesCreated: 0,
})
const finance = ref({
  collectionsCompleted: 0,
  receiptsUploaded: 0,
  expensesApproved: 0,
  financialReconciled: false,
  disputes: 0,
})
const participation = ref({
  attendanceRate: 0,
  activityRate: 0,
  retentionRate: 0,
  responseRate: 0,
})
const marketplace = ref({
  listingsCreated: 0,
  purchasesMade: 0,
  vendorRating: 0,
  successfulTransactions: 0,
  totalTransactions: 0,
})
const improvementTips = ref([])

const healthMeta = computed(() => healthMetaOf(health.value.score))

const links = computed(() => {
  const g = props.groupId
  return {
    dashboard: g ? `/groups/${g}/growth/` : '/community-growth/',
  }
})

const COMPONENT_COLORS = {
  Governance: 'var(--avocash-blue, #3b4fca)',
  Finance: 'var(--avocash-green, #1a6b42)',
  Participation: 'var(--avocash-leaf, #5f8a4a)',
  Marketplace: 'var(--avocash-purple, #6b46c1)',
}

function componentColor(name) {
  return COMPONENT_COLORS[name] || 'var(--avocash-gold, #c9a84c)'
}

function componentScore(name) {
  return health.value.components.find((c) => c.name === name)?.score ?? 0
}

onMounted(async () => {
  try {
    await growthStore.fetchHealth(groupId.value)
  } catch (e) {
    console.warn('Growth health unavailable:', e)
  }

  const data = health.value

  governance.value = data.components.find((c) => c.name === 'Governance')?.details
    || governance.value
  finance.value = data.components.find((c) => c.name === 'Finance')?.details
    || finance.value
  participation.value = data.components.find((c) => c.name === 'Participation')?.details
    || participation.value
  marketplace.value = data.components.find((c) => c.name === 'Marketplace')?.details
    || marketplace.value

  improvementTips.value = generateImprovementTips(data)
})

function generateImprovementTips(healthData) {
  const tips = []

  if (healthData.components.find((c) => c.name === 'Governance')?.score < 80) {
    tips.push({
      id: 1,
      title: t('growth.health.tipGovernanceTitle'),
      description: t('growth.health.tipGovernanceDesc'),
    })
  }

  if (healthData.components.find((c) => c.name === 'Finance')?.score < 80) {
    tips.push({
      id: 2,
      title: t('growth.health.tipFinanceTitle'),
      description: t('growth.health.tipFinanceDesc'),
    })
  }

  if (healthData.components.find((c) => c.name === 'Participation')?.score < 80) {
    tips.push({
      id: 3,
      title: t('growth.health.tipParticipationTitle'),
      description: t('growth.health.tipParticipationDesc'),
    })
  }

  if (healthData.components.find((c) => c.name === 'Marketplace')?.score < 80) {
    tips.push({
      id: 4,
      title: t('growth.health.tipMarketplaceTitle'),
      description: t('growth.health.tipMarketplaceDesc'),
    })
  }

  return tips
}
</script>

<style scoped>
.growth-page {
  --avo-green: var(--f7-theme-color, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--f7-secondary, #c9a84c);
  --avo-gold-light: var(--avocash-gold-light, #fdf6e3);
  --avo-blue: var(--f7-blue, #3b4fca);
  --avo-blue-light: var(--avocash-blue-light, #ebf0fc);
  --avo-terracotta: var(--f7-error, #ba1a1a);
  --avo-terracotta-light: var(--avocash-terracotta-light, #fdecea);
  --avo-leaf: var(--f7-theme-color, #5f8a4a);
  --avo-purple: var(--f7-purple, #6b46c1);
  --avo-purple-light: var(--avocash-purple-light, #f5f0ff);
  --avo-surface: var(--avocash-surface, #f4f7f2);
  --avo-pit: var(--f7-on-surface, #1c1c1e);
}

.loading-block { text-align: center; padding: 40px 0; }

.t-bold { font-weight: 600; }
.t-h2 { font-size: 30px; line-height: 1.1; }
.t-subtitle { font-size: 14px; color: var(--avo-pit); opacity: .65; }
.t-muted { color: var(--f7-on-surface-variant, #6f7a71); }

.t-green { color: var(--avo-green); }
.t-gold { color: var(--avo-gold); }
.t-blue { color: var(--avo-blue); }
.t-leaf { color: var(--avo-leaf); }
.t-purple { color: var(--avo-purple); }
.t-terracotta { color: var(--avo-terracotta); }

.text-green { color: var(--avo-green); }
.text-terracotta { color: var(--avo-terracotta); }

.growth-card { border-radius: 14px; }
.growth-card :deep(.card-header) { font-size: 15px; gap: 8px; }

.health-hero { background: linear-gradient(180deg, var(--avo-surface), transparent); }
.health-ring {
  width: 92px; height: 92px; margin: 0 auto 10px;
  border-radius: 50%; border: 4px solid var(--avo-leaf);
  display: flex; align-items: center; justify-content: center;
  background: var(--f7-card-bg-color, #fff);
}
.health-badge {
  display: inline-flex; padding: 4px 12px; border-radius: 10px;
  font-size: 11px; font-weight: 600; margin-top: 10px;
}

.progress-leaf :deep(.progressbar-inner) { background: var(--avo-leaf); }
.progress-component :deep(.progressbar-inner) { background: var(--component-color); }

.growth-list :deep(.item-title) { font-size: 14px; }
.growth-list :deep(.item-footer) { font-size: 12px; }

.star-row { display: flex; }
.star-empty { color: var(--avo-pit); opacity: .2; }

.btn-green { background: var(--avo-green); color: #fff; }
.btn-green.active-state { opacity: .8; }
</style>
